'use client';

import { useQuery } from '@rocicorp/zero/react';
import { FC, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandList
} from '@components/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@components/ui/dialog';
import { MOBILE_CHROME_BREAKPOINT, useIsMobile } from '@hooks/use-mobile';
import { useKnowledgeSearch } from '@hooks/use-knowledge-search';
import { useSearchCommand } from '@hooks/use-search-command';
import { useAgentsMembershipEnabled } from '@hooks/use-agents-membership';
import { getUserDisplayName } from '@utils/user-utils';
import { queries } from '~/src/zero/queries';
import { useCurrentOrgUser, useCurrentUser } from '~/src/zero/use-current-user';
import type { SearchHit } from '../../search/types';
import AskAboutThisDialog from './AskAboutThisDialog';
import KnowledgeSearchAnswerCard from './KnowledgeSearchAnswerCard';
import KnowledgeSearchDefaultControls from './KnowledgeSearchDefaultControls';
import KnowledgeSearchMobile from './KnowledgeSearchMobile';
import KnowledgeSearchRow, {
  type KnowledgeSearchRowData
} from './KnowledgeSearchRow';
import KnowledgeSearchSkeletonRow from './KnowledgeSearchSkeletonRow';
import { useKnowledgeSearchNavigation } from './knowledge-search-navigation';

type KnowledgeSearchProps = {
  organization_id: string;
};

/** Uppercase source-type label shown on every row (Outcome hierarchy). */
function useTypeLabels() {
  const t = useTranslations('knowledge-search');
  return {
    recipe: t('recipe'),
    ingredient: t('ingredient'),
    menu: t('menu'),
    reference: t('reference'),
    handbook: t('handbook'),
    person: t('person'),
    channel: t('channel')
  } as const;
}

/**
 * Global knowledge search: replaces the previous name-only recipe/ingredient
 * palette. Desktop keeps the ⌘K dialog; mobile opens a full-screen drawer
 * (KnowledgeSearchMobile). Both share `useKnowledgeSearch` (the HTTP-backed
 * controller) and `KnowledgeSearchRow`. People/channel discovery stays on
 * the existing Zero lexical queries, unchanged, per the plan's Step 3.7.
 */
const KnowledgeSearch: FC<KnowledgeSearchProps> = ({ organization_id }) => {
  const t = useTranslations('knowledge-search');
  const { open, setOpen } = useSearchCommand();
  const isMobile = useIsMobile(MOBILE_CHROME_BREAKPOINT);
  const currentUser = useCurrentUser();
  const orgUser = useCurrentOrgUser(organization_id);
  const [organization] = useQuery(
    queries.organizations.current({ organizationId: organization_id })
  );
  const typeLabels = useTypeLabels();
  const navigate = useKnowledgeSearchNavigation(organization_id, isMobile);

  const search = useKnowledgeSearch({
    organizationId: organization_id,
    open,
    currentUserId: currentUser?.id
  });

  const [highlightedHitId, setHighlightedHitId] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<number | undefined>(undefined);
  const agentsMembershipEnabled = useAgentsMembershipEnabled();
  const [askAboutThisOpen, setAskAboutThisOpen] = useState(false);

  /**
   * A citation click scrolls to the row and briefly highlights it rather
   * than navigating away immediately, so the user can see the answer and
   * its evidence together before committing to open one (plan Step 5.2).
   */
  const highlightHit = (hitId: string) => {
    window.clearTimeout(highlightTimeoutRef.current);
    setHighlightedHitId(hitId);
    document
      .querySelector(`[data-hit-id="${CSS.escape(hitId)}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    highlightTimeoutRef.current = window.setTimeout(
      () => setHighlightedHitId(null),
      1500
    );
  };

  const committedPeopleChannelTerm =
    search.term.trim().length >= 2 ? search.term.trim() : '';
  const [peopleResults] = useQuery(
    committedPeopleChannelTerm
      ? queries.people.search({
          organizationId: organization_id,
          term: committedPeopleChannelTerm
        })
      : undefined
  );
  const [channelResults] = useQuery(
    committedPeopleChannelTerm
      ? queries.chatChannels.search({
          organizationId: organization_id,
          term: committedPeopleChannelTerm
        })
      : undefined
  );

  const close = () => {
    setOpen(false);
    search.setTerm('');
  };

  const selectHit = (hit: SearchHit) => {
    close();
    navigate(hit);
  };

  const rows: KnowledgeSearchRowData[] = useMemo(() => {
    const knowledgeRows = search.results.map((hit): KnowledgeSearchRowData => ({
      key: `${hit.kind}:${hit.id}`,
      typeLabel: typeLabels[hit.kind as keyof typeof typeLabels] ?? hit.kind,
      title: hit.title,
      subtitle: hit.subtitle,
      onSelect: () => selectHit(hit),
      hitId: hit.id,
      highlighted: hit.id === highlightedHitId
    }));
    const peopleRows = ((peopleResults ?? []) as any[]).map(
      (membership): KnowledgeSearchRowData => ({
        key: `person:${membership.userId}`,
        typeLabel: typeLabels.person,
        title: getUserDisplayName(membership.user ?? {}),
        subtitle: '',
        onSelect: () => {
          close();
          navigate({
            kind: 'person',
            id: membership.userId,
            title: '',
            subtitle: '',
            sourceVersion: 0,
            destination: { kind: 'person', userId: membership.userId }
          });
        }
      })
    );
    const channelRows = ((channelResults ?? []) as any[]).map(
      (channel): KnowledgeSearchRowData => {
        const otherMember = channel.dmChannel
          ? channel.members?.find(
              (member: any) => member.userId !== orgUser?.userId
            )
          : undefined;
        const title = channel.dmChannel
          ? getUserDisplayName(otherMember?.user ?? {})
          : (channel.name ?? '');
        return {
          key: `channel:${channel.id}`,
          typeLabel: typeLabels.channel,
          title,
          subtitle: '',
          onSelect: () => {
            close();
            navigate({
              kind: 'channel',
              id: channel.id,
              title,
              subtitle: '',
              sourceVersion: 0,
              destination: { kind: 'channel', channelId: channel.id }
            });
          }
        };
      }
    );
    return [...knowledgeRows, ...peopleRows, ...channelRows];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search.results,
    peopleResults,
    channelResults,
    typeLabels,
    orgUser,
    highlightedHitId
  ]);

  const citedSources = useMemo(() => {
    if (!search.answer) return [];
    const hitsById = new Map(search.results.map(hit => [hit.id, hit]));
    return search.answer.citedHitIds
      .map(hitId => hitsById.get(hitId))
      .filter((hit): hit is SearchHit => hit !== undefined)
      .map(hit => ({ kind: hit.kind, id: hit.id, title: hit.title }));
  }, [search.answer, search.results]);

  const onAskAboutThis =
    agentsMembershipEnabled && search.answer
      ? () => setAskAboutThisOpen(true)
      : undefined;

  const loadingLabel = search.isLikelyQuestion
    ? t('findingAnswer')
    : t('loading');

  const emptyMessage =
    search.status === 'loading'
      ? loadingLabel
      : search.status === 'error'
        ? t('error')
        : search.status === 'offline'
          ? t('offline')
          : search.status === 'guidance'
            ? t('guidance')
            : rows.length === 0 && search.effectiveQuery
              ? t('emptyForQuery', { query: search.effectiveQuery })
              : t('empty');

  const defaultLabel =
    search.mode === 'default'
      ? t('defaultLabel', { query: search.effectiveQuery })
      : search.mode === 'recommended'
        ? t('recommended')
        : null;

  const askAboutThisDialog = onAskAboutThis ? (
    <AskAboutThisDialog
      organizationId={organization_id}
      open={askAboutThisOpen}
      onOpenChange={setAskAboutThisOpen}
      question={search.effectiveQuery || search.term}
      sources={citedSources}
    />
  ) : null;

  if (isMobile) {
    return (
      <>
        <KnowledgeSearchMobile
          open={open}
          onOpenChange={next => (next ? setOpen(true) : close())}
          organizationName={organization?.name}
          term={search.term}
          onTermChange={search.setTerm}
          onSubmit={search.submit}
          rows={rows}
          emptyMessage={rows.length === 0 ? emptyMessage : null}
          loadingLabel={loadingLabel}
          defaultLabel={defaultLabel}
          status={search.status}
          onRetry={search.retry}
          mode={search.mode}
          saveStatus={search.saveStatus}
          onSaveAsDefault={search.saveAsDefault}
          onResetDefault={search.resetDefault}
          answer={search.answer}
          results={search.results}
          onCitationClick={highlightHit}
          onAskAboutThis={onAskAboutThis}
        />
        {askAboutThisDialog}
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={next => (next ? setOpen(true) : close())}
      >
        <DialogContent
          aria-describedby={undefined}
          className="flex max-h-[85dvh] max-w-[720px] flex-col gap-0 overflow-hidden p-0"
        >
          <DialogTitle className="sr-only">{t('heading')}</DialogTitle>
          <div className="border-b border-border px-6 pb-4 pt-6">
            {organization?.name ? (
              <div className="text-column uppercase tracking-wider text-muted-foreground">
                {organization.name}
              </div>
            ) : null}
            <h2 className="mt-1 text-page-title text-foreground">
              {t('heading')}
            </h2>
          </div>
          <Command
            shouldFilter={false}
            className="flex-1 overflow-hidden bg-popover pt-2"
          >
            <CommandInput
              placeholder={t('placeholder')}
              value={search.term}
              onValueChange={search.setTerm}
              className="h-14 text-body"
              onKeyDown={event => {
                if (event.key === 'Enter') search.submit();
              }}
            />
            {defaultLabel ? (
              <div className="flex items-center justify-between px-4 pt-3">
                <span className="text-caption text-muted-foreground">
                  {defaultLabel}
                </span>
                <KnowledgeSearchDefaultControls
                  mode={search.mode}
                  status={search.status}
                  termLength={search.term.trim().length}
                  saveStatus={search.saveStatus}
                  onSave={search.saveAsDefault}
                  onReset={search.resetDefault}
                />
              </div>
            ) : (
              <KnowledgeSearchDefaultControls
                mode={search.mode}
                status={search.status}
                termLength={search.term.trim().length}
                saveStatus={search.saveStatus}
                onSave={search.saveAsDefault}
                onReset={search.resetDefault}
              />
            )}
            {search.answer ? (
              <KnowledgeSearchAnswerCard
                answerText={search.answer.text}
                citedHitIds={search.answer.citedHitIds}
                results={search.results}
                onCitationClick={highlightHit}
                onAskAboutThis={onAskAboutThis}
              />
            ) : null}
            <CommandList className="min-h-[320px] max-h-[calc(85dvh-160px)]">
              {search.status === 'loading' ? (
                <>
                  <div className="px-4 pb-2 pt-3 text-caption text-muted-foreground">
                    {loadingLabel}
                  </div>
                  <KnowledgeSearchSkeletonRow />
                  <KnowledgeSearchSkeletonRow />
                  <KnowledgeSearchSkeletonRow />
                  <KnowledgeSearchSkeletonRow />
                </>
              ) : rows.length === 0 ? (
                <CommandEmpty
                  role={search.status === 'error' ? 'alert' : undefined}
                >
                  {emptyMessage}
                </CommandEmpty>
              ) : (
                rows.map(({ key, ...row }) => (
                  <KnowledgeSearchRow key={key} {...row} />
                ))
              )}
            </CommandList>
            {search.hasMore ? (
              <div className="border-t border-border px-4 py-2 text-caption text-muted-foreground">
                {t('refineHint')}
              </div>
            ) : null}
          </Command>
        </DialogContent>
      </Dialog>
      {askAboutThisDialog}
    </>
  );
};

export default KnowledgeSearch;
